import { container, singleton } from "tsyringe";
import { BehaviorSubject } from 'rxjs';
import { Config } from "../config";
import { fetchAndThrowHttpErrors, sleep } from '../utils/helpers';
import { PtMeeting } from '../models/meetingClass';
import { UserService } from "./user-service";
import { ptEvent, ptEventDetails, ptEventJoinUrls, ptModeratorInfo, ptPublicEvent } from "../models/ptEvent";
import { Poll, PollCreationInformation, PollInstanceStatus } from "../models/poll";
import { PTSocketService } from "./ptsocket.service";
import { PTChatMessage } from "../models/ptChat";
import { SEAMApiService } from "./seamapi-service";
import { QnaAnswer, QnaQuestion } from "../models/qna";
import moment from "moment";

@singleton()
export class PTApiService {

  private ptUserToken$ = new BehaviorSubject<string>(null);
  public ptEvents$ = new BehaviorSubject<ptEvent[]>(null);
  private ptEventToken$ = new BehaviorSubject<string>(null);
  private ptEventJoinUrls$ = new BehaviorSubject<ptEventJoinUrls>(null);

  public videoSharingInitial$ = new BehaviorSubject<any>(null);
  public ptCurrentEventInformation$ = new BehaviorSubject<[ptPublicEvent, ptEventDetails]>(null);
  private ptapiUrl = `${Config.PTAPI_URL}/api`;
  public ptMeeting: PtMeeting = null;
  private moderatorInfo: ptModeratorInfo = null;

  // using DI in constructor would require to eject the app
  private userService = container.resolve(UserService);
  private ptSocketService = container.resolve(PTSocketService);
  private seamApiService = container.resolve(SEAMApiService);

  public get ptEventsObservable() { return this.ptEvents$.asObservable(); }

  public get ptEventJoinUrlsObservable() { return this.ptEventJoinUrls$.asObservable(); }
  public get ptEventJoinUrlsCurrentValue() { return this.ptEventJoinUrls$.value; }

  public get studioModeratorJoinUrl() {
    if (!this.ptCurrentEventInformation$.value) {
      return;
    }

    return `${Config.DASHBOARD_URL}${process.env.REACT_APP_ROUTING_BASE_URL || process.env.PUBLIC_URL || '/'}event/${this.ptCurrentEventInformation$.value[0].sharingUrl}`;
  }
  public addPTUserAuthenticationHeader(headers: any) {
    if (!this.ptUserToken$.value) {
      throw new Error('addPTUserAuthenticationHeader error : no valid token');
    }
    headers['Authorization'] = `Bearer ${this.ptUserToken$.value}`;
    return headers;
  }

  public async retrievePtUserToken() {
    // const newUserToken = await fetchAndThrowHttpErrors(`${this.ptapiUrl}/security/v1/oauth/accesstoken`, {
    //   method: 'POST',
    //   headers: {},
    //   body: JSON.stringify({
    //     grant_type: 'a2m_user',
    //     userRole: 'user',
    //     properties: {
    //       userAccessToken: this.userService.bjToken$.value
    //     }
    //   })
    // }) as { access_token: string };

    // this.ptUserToken$.next(newUserToken.access_token);
  }

  public async loadEvents(nextPageKey = '1') {
    if (!this.ptUserToken$.value) {
      await this.retrievePtUserToken();
    }

    const eventsResult = await fetchAndThrowHttpErrors(`${this.ptapiUrl}/scheduling/v1/users/${this.userService.user.id}/events?sortBy=start&order=asc&from=${moment().valueOf()}&nextPageKey=${nextPageKey}&count=20`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${this.ptUserToken$.value}`
      }
    }) as { nextPageKey: string, events: ptEvent[], expiredEventGrace: number };

    this.ptEvents$.next(eventsResult.events);

    return eventsResult;
  }

  private async getEventAccessToken(eventId: string, moderatorUrl: string) {
    const eventToken = await fetchAndThrowHttpErrors(`${this.ptapiUrl}/security/v1/oauth/accesstoken`, {
      method: 'POST',
      body: JSON.stringify({
        eventId: eventId,
        grant_type: 'a2mevent',
        properties: {
          emailId: this.userService.user.emailId,
          sharingUrl: moderatorUrl,
          userAccessToken: this.userService.bjToken$.value
        },
        userRole: 'moderator'
      })
    }) as { access_token: string };


    console.log('PTAPI event token', eventToken);

    this.ptEventToken$.next(eventToken.access_token);

    return eventToken.access_token;
  }

  public getEventToken() {
    return this.ptEventToken$.getValue();
  }

  public async waitForEventInstanciated() {
    if (this.ptCurrentEventInformation$) {
      const [event] = this.ptCurrentEventInformation$.getValue();

      const newEvent = await fetchAndThrowHttpErrors(`${this.ptapiUrl}/eventdetails/v1/events/public/details?sharingUrl=${event.sharingUrl}`);
      if (newEvent.instanceId) {
        await this.getEvent(event.eventId, event.sharingUrl);
        return newEvent.instanceId;
      }
    }

    await sleep(2000);
    return await this.waitForEventInstanciated();
  }

  public async getPublicEventFromCode(moderatorUrl: string) {
    return fetchAndThrowHttpErrors(`${this.ptapiUrl}/eventdetails/v1/events/public/details?sharingUrl=${moderatorUrl}`) as Promise<ptPublicEvent>;
  }

  private async getEvent(eventId: string, moderatorUrl: string) {

    let event: [ptPublicEvent, ptEventDetails];
    if (eventId !== 'demodemo') {

      if (!this.ptUserToken$.value) {
        await this.retrievePtUserToken();
      }
      event = await Promise.all([
        this.getPublicEventFromCode(moderatorUrl),
        fetchAndThrowHttpErrors(`${this.ptapiUrl}/eventdetails/v1/events/${eventId}/details`, {
          headers: {
            Authorization: `Bearer ${this.ptEventToken$.value}`
          }
        })
        // fetchAndThrowHttpErrors(`${this.ptapiUrl}/scheduling/v1/users/${this.userService.user.id}/events/${eventId}`, {
        //   headers: {
        //     Authorization: `Bearer ${this.ptUserToken$.value}`
        //   }
        // })
      ]) as [ptPublicEvent, ptEventDetails];

      this.ptCurrentEventInformation$.next(event);

    } else {

      event =
        [
          {
          allowInvitedAttendeesOnly: false,
          authenticationType: "PASSWORD",
          baseApiUrl: "",
          billingCapacity: {
            attendeeLimitReached: false,
            durationLimitReached: false,
            interactiveLimitReached: false
          },
          description: "demodemo",
          enableRegistration: false,
          end: 1645156800000,
          enterpriseId: 3560,
          eventId: "f80ba647-f41f-488f-8aa5-5aa014efb109",
          eventSocketUrl: "",
          eventTitle: "DEMO event",
          instanceId: null,
          joinUrl: "",
          loggerServiceUrl: "",
          onPremBaseUrl: "",
          pollShortUrl: "",
          pstnEnabled: false,
          recurrencePattern: {
            dayOfMonth: 0,
            daysOfWeekMask: 0,
            endDate: null,
            frequency: 0,
            monthOfYear: "NONE",
            recurrenceCount: 0,
            recurrenceType: "NONE",
            weekOfMonth: "NONE"
          },
          recurrenceStart: null,
          requireEmail: false,
          restricted: false,
          restrictedInviteOnly: false,
          role: "Moderator",
          sharingUrl: "demodemo",
          start: 1645153200000,
          studioEnabled: true,
          systemCapacity: null,
          timezone: "US/Pacific",
          tzShortName: "PST",
          whitelistedDomains: "*.bluejeans.com,*.bluejeansdev.com",
          },
          {
          allowInvitedAttendeesOnly: false,
          askAnonymous: true,
          attendeeCount: 0,
          attendeeMeetingId: 39455,
          attendeeMeetingNumericId: "123456789",
          attendeeMeetingPasscode: "1234",
          attendeeRoster: true,
          attendeeRosterDisabled: false,
          autoRecord: true,
          baseApiUrl: "",
          chatEnabled: true,
          currentServerTime: 1645177723401,
          customIntroOutro: false,
          description: null,
          dialInNumbers: {alternate: null, us: '+1 (408) 757-0975', us_tollfree: null, US: '+1 (408) 757-0975', US_Tollfree: null},
          doNotShowRoomSystemInfo: false,
          enableClosedCaption: false,
          enableRegistration: false,
          enableTranslation: false,
          end: 1645156800000,
          errorMode: false,
          eventSocketUrl: "",
          eventTitle: "New test Event -- 1234",
          eventUserVBFeature: {enabled: true, showDefaultImagesToUser: true},
          greenroomEnabled: false,
          id: "f80ba647-f41f-488f-8aa5-5aa014efb109",
          introOutro: {introImgUrl: '', outroImgUrl: '', backgroundImgUrl: '', introInterval: 15, outroInterval: 15, introImageId: null, outroImageId: null },
          introOutroEnabled: true,
          lastMeetingTerminatedTime: 0,
          lastModeratorLeaveTime: -1,
          lastStreamingStart: 0,
          leaderId: 13689,
          liveInterpretation: false,
          meetingActive: false,
          meetingNumericId: "123456789",
          moderatorPasscode: "12345",
          onPremBaseUrl: "",
          one2OneModChat: true,
          organizerEnterpriseId: 3560,
          organizerId: 24679,
          originalEndDate: 1645156800000,
          originalStartDate: 1645153200000,
          participantPasscode: "8355",
          playerBackgroundAudio: true,
          pstnEnabled: false,
          qnaAutoApprove: true,
          qnaEnabled: true,
          raiseHandEnabled: false,
          recordingActive: false,
          recurrenceEndNever: false,
          requireEmail: false,
          restricted: false,
          scheduledTimeZone: "America/Los_Angeles",
          showAttendeeCount: false,
          start: 1645153200000,
          state: "NOT_STARTED",
          streamingDuration: 0,
          studioEnabled: true,
          systemParams: {attendeePushLimit: 100, attendeePushDiff: 25},
          timezone: "US/Pacific",
          translationLanguages: {},
          tzShortName: "PST",
          waterMarkEnabled: false,
          whitelistedDomains: "*.bluejeans.com,*.bluejeansdev.com",
          }
        ];

      this.ptCurrentEventInformation$.next(event);
    }

    // get videoSharingDetails - only at creation
    this.videoSharingInitial$.next(await this.getVideoSharingDetails());

    return event;
  }

  // Retrieve the meetingId and passcode with the minimum API calls
  public async retrieveEventMeetingInformation(eventId: string, moderatorUrl: string) {
    await this.getEventAccessToken(eventId, moderatorUrl);
    return await this.getEvent(eventId, moderatorUrl);
  }

  // Retrieve the moderatorUrl and connect to Event
  public async connectToEvent(eventId: string, moderatorUrl: string) {
    await this.retrievePtUserToken();
    // await this.sendPrejoinRequest(eventId, moderatorUrl);

    // const publicEvent = await fetchAndThrowHttpErrors(`${this.ptapiUrl}/eventdetails/v1/events/public/details?sharingUrl=${moderatorUrl}`) as ptPublicEvent;

    // await this.ptSocketService.connectSocket(eventId, this.ptEventToken$.value, publicEvent.eventSocketUrl);

    const eventData = await this.getEvent(eventId, moderatorUrl);
    // await this.getJoinUrl();
    return eventData;
  }

  public async sendPrejoinRequest(eventId: string, moderatorUrl: string) {
    const eventAccessToken = await this.getEventAccessToken(eventId, moderatorUrl);

    // Proceed to pre-join. It will instanciate the event and allow to have an instanceId in the event details.
    await fetchAndThrowHttpErrors(`${this.ptapiUrl}/eventuser/v1/events/${eventId}/moderator/pre-join`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${eventAccessToken}`
      }
    });
  }

  public async registerAsModerator(ptSocketGuid: string, seamGuid: string) {
    const event = this.ptCurrentEventInformation$.value[0];
    const response = await fetchAndThrowHttpErrors(`${this.ptapiUrl}/eventuser/v1/events/${event.eventId}/instance/${event.instanceId}/moderator`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${this.ptEventToken$.value}`
      },
      body: JSON.stringify({
        name: `${this.userService.user.firstName} ${this.userService.user.lastName}`,
        endpointGUID: ptSocketGuid,
        details: {
          emailId: this.userService.user.emailId,
          profilePictureURI: 'http://image.bluejeansnet.com',
          linkedinURI: '',
          twitterId: ''
        },
        // TODO put actual values
        endpointDetail: {
          browser: 'chrome',
          os: 'mac',
          flashVersion: '1.0',
          epType: 'WebRTC'
        },
        interactivityDetail: {
          endpointGUID: seamGuid
        }
      })
    }) as ptModeratorInfo;

    this.moderatorInfo = response;

    console.log('user is now registered as MODERATOR', this.moderatorInfo);

    return response;
  }

  // participant
  public async getRosterInteractive() {
    try {

      const participants = await fetchAndThrowHttpErrors(`${this.ptapiUrl}/eventuser/v1/events/${this.ptCurrentEventInformation$.value[0].eventId}/instance/${this.ptCurrentEventInformation$.value[0].instanceId}/live/roster/interactive?=${Date.now()}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${this.ptEventToken$.value}`
        }
      });

      console.log('ROSTER participants', participants)

      return participants.roster;
    } catch (e) {
      return [];
    }

  }


  // attendee
  public async getRosterAttendees() {
    try {
      const attendees = await fetchAndThrowHttpErrors(`${this.ptapiUrl}/eventuser/v1/events/${this.ptCurrentEventInformation$.value[0].eventId}/instance/${this.ptCurrentEventInformation$.value[0].instanceId}/live/roster/attendee?pageSize=99&sortByField=JOINTIME&sortBy=DESCENDING&_=${Date.now()}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${this.ptEventToken$.value}`
        }
      });

      console.log('ROSTER attendees', attendees)
      return attendees.roster;
    } catch (e) {
      return [];
    }
  }

  public async getJoinUrl() {
    const joinUrls = await fetchAndThrowHttpErrors(`${this.ptapiUrl}/eventdetails/v1/events/${this.ptCurrentEventInformation$.value[0].eventId}/joinurl`, {
      headers: {
        Authorization: `Bearer ${this.ptEventToken$.value}`
      }
    }) as ptEventJoinUrls;

    this.ptEventJoinUrls$.next(joinUrls);

    return joinUrls;
  }

  // GO LIVE
  //

  public async startBroadcast() {
    return await fetchAndThrowHttpErrors(`${this.ptapiUrl}/manager/v1/events/${this.ptCurrentEventInformation$.value[0].eventId}/manage/inevent`, {
      method: 'POST',
      body: JSON.stringify({ eventId: this.ptCurrentEventInformation$.value[0].eventId }),
      headers: {
        Authorization: `Bearer ${this.ptEventToken$.value}`
      }
    });
  }

  public async stopBroadcast() {
    return await fetchAndThrowHttpErrors(`${this.ptapiUrl}/manager/v1/events/${this.ptCurrentEventInformation$.value[0].eventId}/manage/pauseevent`, {
      method: 'POST',
      body: JSON.stringify({ eventId: this.ptCurrentEventInformation$.value[0].eventId }),
      headers: {
        Authorization: `Bearer ${this.ptEventToken$.value}`
      }
    });
  }

  // Polls
  //

  // Strong validation to avoid sending bad data to server.
  // There is no server side check and sending bad data can completely
  // block the PT Event.
  private validatePollInformation(poll: PollCreationInformation) {
    if (!poll.question) {
      throw new Error('Poll question is empty');
    }

    if (poll.question.length > 150) {
      throw new Error('Poll question is too long');
    }

    if (!poll.options || poll.options.length < 2) {
      throw new Error('Poll has a too few options');
    }

    for (let i = 1; i <= poll.options.length; i++) {
      const opt = poll.options.find(o => o.optionNumber === i);
      if (!opt) {
        throw new Error('Poll options are not properly organized');
      }

      if (!opt.option) {
        throw new Error('Poll option text is not set');
      }

      if (opt.option.length > 150) {
        throw new Error('Poll option text is too long');
      }
    }
  }

  public async createPoll(poll: PollCreationInformation) {
    this.validatePollInformation(poll);
    const createdPoll = await fetchAndThrowHttpErrors(`${this.ptapiUrl}/polling/v1/events/${this.ptCurrentEventInformation$.value[0].eventId}/polls`, {
      method: 'POST',
      body: JSON.stringify(poll),
      headers: {
        Authorization: `Bearer ${this.ptEventToken$.value}`
      }
    }) as { pollGuid: string };

    return createdPoll;
  }

  public async startPoll(pollId: string) {
    const startedPoll = await fetchAndThrowHttpErrors(`${this.ptapiUrl}/polling/v1/events/${this.ptCurrentEventInformation$.value[0].eventId}/instances/${this.ptCurrentEventInformation$.value[0].instanceId}/polls/${pollId}/manage`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${this.ptEventToken$.value}`
      },
      body: JSON.stringify({
        action: 'START',
        initiatorName: `${this.userService.user$.value.firstName} ${this.userService.user$.value.lastName}`,
        pollInstanceGuid: null
      })
    }) as {
      pollInstanceGuid: string;
      pollGuid: string;
      pollInstanceStatus: PollInstanceStatus;
      pollInstanceQuestion: string;
    };

    return startedPoll;
  }

  public async editPoll(pollId: string, poll: PollCreationInformation) {
    this.validatePollInformation(poll);
    await fetchAndThrowHttpErrors(`${this.ptapiUrl}/polling/v1/events/${this.ptCurrentEventInformation$.value[0].eventId}/polls/${pollId}`, {
      method: 'PUT',
      body: JSON.stringify(poll),
      headers: {
        Authorization: `Bearer ${this.ptEventToken$.value}`
      }
    });
  }

  public async fetchPolls(status: PollInstanceStatus) {
    // This endpoint returns HTTP error codes such as 404 when there is no poll for the given status
    try {
      // const poll = await fetchAndThrowHttpErrors(`${this.ptapiUrl}/polling/v1/events/${this.ptCurrentEventInformation$.value[0].eventId}/instances/${this.ptCurrentEventInformation$.value[0].instanceId}/polls/${status.toLocaleLowerCase()}`, {
      //   method: 'GET',
      //   headers: {
      //     Authorization: `Bearer ${this.ptEventToken$.value}`
      //   }
      // });

      // let polls: Poll[] = [];
      // if (status === PollInstanceStatus.AVAILABLE) {
      //   polls = (<{ nextPageKey: any, pollInfo: Poll[] }>poll).pollInfo;
      // } else if (status === PollInstanceStatus.ACTIVE) {
      //   polls = [poll as Poll];
      // } else if (status === PollInstanceStatus.COMPLETED) {
      //   polls = (<{ completedPolls: Poll[] }>poll).completedPolls;
      // }

      // return polls;
      return  [];
    } catch (e) {
      return null;
    }
  }

  public async endPoll(pollId: string, pollInstanceId: string) {
    const startedPoll = await fetchAndThrowHttpErrors(`${this.ptapiUrl}/polling/v1/events/${this.ptCurrentEventInformation$.value[0].eventId}/instances/${this.ptCurrentEventInformation$.value[0].instanceId}/polls/${pollId}/manage`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${this.ptEventToken$.value}`
      },
      body: JSON.stringify({
        action: 'END',
        initiatorName: `${this.userService.user$.value.firstName} ${this.userService.user$.value.lastName}`,
        pollInstanceGuid: pollInstanceId
      })
    }) as {
      pollInstanceGuid: string;
      pollGuid: string;
      pollInstanceStatus: PollInstanceStatus;
      pollInstanceQuestion: string;
    };

    return startedPoll;
  }

  public async deletePoll(pollId: string) {
    await fetchAndThrowHttpErrors(`${this.ptapiUrl}/polling/v1/events/${this.ptCurrentEventInformation$.value[0].eventId}/polls/${pollId}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${this.ptEventToken$.value}`
      }
    });
  }

  // CHATS
  //

  public async getModeratorGroupChatHistory() {
    const event = this.ptCurrentEventInformation$.value[0];
    const history = await fetchAndThrowHttpErrors(`${this.ptapiUrl}/chat/v1/events/${event.eventId}/instances/${event.instanceId}/moderatorchat/scrollback`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${this.ptEventToken$.value}`
      }
    }) as PTChatMessage[];

    return history;
  }

  public async getBackstageChatHistory() {
    if (this.ptCurrentEventInformation$ && this.ptCurrentEventInformation$.value) {
      const event = this.ptCurrentEventInformation$?.value[0];
      if (event) {
        const history = await fetchAndThrowHttpErrors(`${this.ptapiUrl}/chat/v1/events/${event.eventId}/instances/${event.instanceId}/moderator/presenter/chat/scrollback`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${this.ptEventToken$.value}`
          }
        }) as PTChatMessage[];

        return history;
      }
    }
    return [];
  }

  public async getModeratorChatHistory() {
    if (this.ptCurrentEventInformation$ && this.ptCurrentEventInformation$.value) {
      const event = this.ptCurrentEventInformation$?.value[0];
      if (event) {
        const history = await fetchAndThrowHttpErrors(`${this.ptapiUrl}/chat/v1/events/${event.eventId}/instances/${event.instanceId}/moderatorchat/scrollback`, {

          method: 'GET',
          headers: {
            Authorization: `Bearer ${this.ptEventToken$.value}`
          }
        }) as PTChatMessage[];

        return history;
      }
    }
    return [];
  }

  public async getGeneralChatHistory(privateMessages = false, endpointGuid: string = null) {
    if (this.ptCurrentEventInformation$ && this.ptCurrentEventInformation$.value) {
      const event = this.ptCurrentEventInformation$?.value[0];
      if (event && event.instanceId) {
        // This endpoint can be adapted for private messages with 'private' query param
        const history = await fetchAndThrowHttpErrors(`${this.ptapiUrl}/chat/v1/events/${event.eventId}/chat/scrollback/moderator/${endpointGuid ? endpointGuid : event.instanceId}${privateMessages ? '?private=true' : ''}`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${this.ptEventToken$.value}`
          }
        }) as PTChatMessage[];

        return history;
      }
      return [];
    }
    return [];
  }

  public async getGeneralChatHistoryWithCount() {
    const event = this.ptCurrentEventInformation$.value[0];
    // This endpoint can be adapted for private messages with 'private' query param
    const history = await fetchAndThrowHttpErrors(`${this.ptapiUrl}/chat/v1/events/${event.eventId}/chat/moderator/${event.instanceId}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${this.ptEventToken$.value}`
      }
    }) as { messages: PTChatMessage[], totalChatCount: number; };

    return history;
  }

  public async getLastPrivateMessages() {
    // const event = this.ptCurrentEventInformation$.value[0];
    const latests = [];
    //  await fetchAndThrowHttpErrors(`${this.ptapiUrl}/chat/v1/events/${event.eventId}/chat/private/scrollback?numberOfRecords=1`, {
    //   method: 'GET',
    //   headers: {
    //     Authorization: `Bearer ${this.ptEventToken$.value}`
    //   }
    // }) as PTChatMessage[];

    return latests;
  }

  public async deleteChatMessage(messageId: string) {
    const event = this.ptCurrentEventInformation$.value[0];
    await fetchAndThrowHttpErrors(`${this.ptapiUrl}/chat/v1/events/${event.eventId}/chat/${messageId}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${this.ptEventToken$.value}`,
        Accept: null
      },
    });
  }

  // PARTICIPANTS
  //

  public async shareVideoAction(id: any, action: string, offset = 0) {
    if (!this.ptUserToken$.value) {
      await this.retrievePtUserToken();
    }

    if (!this.moderatorInfo) return;

    // console.log('shared video action', id, action, offset);

    const event = this.ptCurrentEventInformation$?.value[0];
    const moderatorGuid = this.moderatorInfo.endpointGUID;
    let contentId = 0;
    try {
      contentId = typeof id === 'string' ? parseInt(id) : id;
    } catch (e) { }

    const body = {
      contentId: contentId,
      endpointGuid: this.ptSocketService.socketGuidValue,
      offset: offset,
      userAccessToken: this.userService.bjToken$.getValue(),
    };

    await fetchAndThrowHttpErrors(`${this.ptapiUrl}/eventuser/v1/events/${event.eventId}/instance/${event.instanceId}/moderator/${moderatorGuid}/sharevideo?action=${action}`, {
      method: 'PUT',
      body: JSON.stringify(body),
      headers: {
        Authorization: `Bearer ${this.ptEventToken$.value}`
      }
    });
  }

  public async getVideoSharingDetails() {
    if (!this.ptUserToken$.value) {
      await this.retrievePtUserToken();
    }
    const event = this.ptCurrentEventInformation$?.value[0];

    // const sharedVideo = await fetchAndThrowHttpErrors(`${this.ptapiUrl}/eventdetails/v1/events/${event.eventId}/videoshare/stats`, {
    //   headers: {
    //     Authorization: `Bearer ${this.ptEventToken$.value}`
    //   }
    // });
    const sharedVideo = null;
    return sharedVideo;
  }

  public async renameParticipantByGuid(newName: string, guid: string) {

    if (!this.ptUserToken$.value) {
      await this.retrievePtUserToken();
    }

    if (!this.moderatorInfo) return;
    if (!guid || guid == '') return;

    const event = this.ptCurrentEventInformation$?.value[0];
    const moderatorGuid = this.moderatorInfo.endpointGUID;

    const body = {
      name: newName,
    };

    await fetchAndThrowHttpErrors(`${this.ptapiUrl}/eventuser/v1/events/${event.eventId}/instance/${event.instanceId}/moderator/${moderatorGuid}/rename/${guid}`, {
      method: 'PUT',
      body: JSON.stringify(body),
      headers: {
        Authorization: `Bearer ${this.ptEventToken$.value}`
      }
    });

  }


  public async getAttendeeCount() {
    // if (this.ptCurrentEventInformation$.value) {

    //   const event = this.ptCurrentEventInformation$.value[0];
    //   return await fetchAndThrowHttpErrors(`${this.ptapiUrl}/eventuser/v1/events/${event.eventId}/instance/${event.instanceId}/attendeecount`, {
    //     method: 'GET',
    //     headers: {
    //       Authorization: `Bearer ${this.ptEventToken$.value}`
    //     }
    //   }) as { attendeeCount: number, interactiveCount: any };
    // }
    return { attendeeCount: 0, interactiveCount: 0 };
  }

  public async setPresentersMute(mute: boolean) {
    const moderatorGuid = this.moderatorInfo.endpointGUID;

    const event = this.ptCurrentEventInformation$.value[0];
    const media = 'audio';
    await fetchAndThrowHttpErrors(`${this.ptapiUrl}/eventuser/v1/events/${event.eventId}/instance/${event.instanceId}/moderator/${moderatorGuid}/endpoints?media=${media}&mute=${mute ? 'true' : 'false'}`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${this.ptEventToken$.value}`
      }
    });
  }

  public async setParticipantMute(endpointGuid: string, media: 'video' | 'audio', mute: boolean, hard: boolean) {

    if (!endpointGuid || endpointGuid.length <= 6) return;

    const moderatorGuid = this.moderatorInfo.endpointGUID;

    const muteMedia = 'mute' + (media.toLowerCase() === 'video' ? 'Video' : 'Audio');
    const muteMode = mute ? (hard ? 'HARD_MUTE' : 'SOFT_MUTE') : 'UNMUTE';

    if (muteMedia === 'Video') {
      await this.setConstancePresenceLayout();
    }

    const event = this.ptCurrentEventInformation$.value[0];
    if (!event.instanceId) {
      console.warn('setParticipantMute() called too soon before event instanciation! call aborted');
      return;
    }
    await fetchAndThrowHttpErrors(`${this.ptapiUrl}/eventuser/v1/events/${event.eventId}/instance/${event.instanceId}/moderator/${moderatorGuid}/endpoint/${endpointGuid}?${muteMedia}=${muteMode}`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${this.ptEventToken$.value}`
      }
    });
  }

  public async setVoiceProcessing(endpointGuid, state) {

    const moderatorGuid = this.moderatorInfo.endpointGUID;

    const event = this.ptCurrentEventInformation$.value[0];
    await fetchAndThrowHttpErrors(`${this.ptapiUrl}/eventuser/v1/events/${event.eventId}/instance/${event.instanceId}/moderator/${moderatorGuid}/endpoint/${endpointGuid}?voiceProcessing=${state ? 'true' : 'false'}`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${this.ptEventToken$.value}`
      }
    });
  }


  public async kickoutParticipant(endpointGuid, role) {

    const moderatorGuid = this.moderatorInfo.endpointGUID;
    const event = this.ptCurrentEventInformation$.value[0];

    await fetchAndThrowHttpErrors(`${this.ptapiUrl}/eventuser/v1/events/${event.eventId}/instance/${event.instanceId}/moderator/kickout/${role.toLowerCase()}/${endpointGuid}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${this.ptEventToken$.value}`
      }
    });
  }

  public async setConstancePresenceLayout() {

    // to be called when video mute change

    const event = this.ptCurrentEventInformation$.value[0];
    if (!this.moderatorInfo) return;
    const moderatorGuid = this.moderatorInfo.endpointGUID;

    const body = {
      layoutMode: "Video",
      subLayoutMode: "VideoConstantPresence"
    };

    // try {
    //   await fetchAndThrowHttpErrors(`${this.ptapiUrl}/eventuser/v1/events/${event.eventId}/instance/${event.instanceId}/moderator/${moderatorGuid}/layout?push=true`, {
    //     method: 'PUT',
    //     body: JSON.stringify(body),
    //     headers: {
    //       Authorization: `Bearer ${this.ptEventToken$.value}`
    //     }
    //   });
    // } catch (e) {
    //   console.warn('PT api sent an error on setConstancePresenceLayout()');
    // }
  }


  // Q&A
  //

  public async fetchQuestions(filter?: 'approved' | 'unanswered' | 'answered') {
    // const event = this.ptCurrentEventInformation$.value[0];
    // const filterParam = filter ? `&filter=${filter}` : '';
    // return await fetchAndThrowHttpErrors(`${this.ptapiUrl}/chat/v1/events/${event.eventId}/qna/question/all?pageNumber=1&pageSize=100${filterParam}`, {
    //   method: 'GET',
    //   headers: {
    //     Authorization: `Bearer ${this.ptEventToken$.value}`,
    //     'Content-Type': null,
    //     'Accept': null
    //   }
    // }) as { nextPageKey: any, questions: QnaQuestion[] };
  }

  public async deleteQuestion(questionId: string) {
    const event = this.ptCurrentEventInformation$.value[0];
    await fetchAndThrowHttpErrors(`${this.ptapiUrl}/chat/v1/events/${event.eventId}/qna/question/${questionId}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${this.ptEventToken$.value}`,
        'Accept': null
      }
    });
  }

  public async upvoteQuestion(questionId: string) {
    const event = this.ptCurrentEventInformation$.value[0];
    return await fetchAndThrowHttpErrors(`${this.ptapiUrl}/chat/v1/events/${event.eventId}/qna/question/${questionId}`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${this.ptEventToken$.value}`,
        'Accept': null
      }
    });
  }

  public async postAnswer(questionId: string, answer: string) {
    const event = this.ptCurrentEventInformation$.value[0];
    await fetchAndThrowHttpErrors(`${this.ptapiUrl}/chat/v1/events/${event.eventId}/qna/question/${questionId}/answer`, {
      method: 'POST',
      body: JSON.stringify({
        content: answer,
        answerBy: {
          name: `${this.userService.user.firstName} ${this.userService.user.lastName}`,
          endpointGuid: this.ptSocketService.socketGuidValue
        }
      }),
      headers: {
        Authorization: `Bearer ${this.ptEventToken$.value}`,
        'Accept': null
      }
    });
  }

  public async approveDeclineQuestion(questionId: string, approve: boolean) {
    const event = this.ptCurrentEventInformation$.value[0];
    await fetchAndThrowHttpErrors(`${this.ptapiUrl}/chat/v1/events/${event.eventId}/qna/question/${questionId}`, {
      method: 'PUT',
      body: JSON.stringify({ approve }),
      headers: {
        Authorization: `Bearer ${this.ptEventToken$.value}`,
        'Accept': null
      }
    });
  }

  public async fetchAnswers(questionId: string) {
    const event = this.ptCurrentEventInformation$.value[0];
    return await fetchAndThrowHttpErrors(`${this.ptapiUrl}/chat/v1/events/${event.eventId}/qna/question/${questionId}/answer`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${this.ptEventToken$.value}`,
        'Accept': null
      }
    }) as QnaAnswer[];
  }

  public async sendAnnouncement(message: string) {
    const event = this.ptCurrentEventInformation$.value[0];
    await fetchAndThrowHttpErrors(`${this.ptapiUrl}/manager/v1/events/${event.eventId}/manage/banner`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${this.ptEventToken$.value}`,
        'Accept': null
      },
      body: JSON.stringify({ bannerMessage: message })
    });
  }
}
